/**
 * Server truth on screen: the 3D canvas, the system-type legend (only
 * types actually present -- a legend coloring nothing is noise),
 * click-to-inspect, and the SELECTED element inspector (a draggable widget).
 *
 * The scope view (DESIGN_PRINCIPLES 33 "scope is the data budget") navigates the spatial ladder.
 * At each rung, the depth determines what the wire carries: counts by plane, thin rows, or whole
 * elements with findings. The viewer, catalog, and findings all read at the same rung.
 *
 * 2026-09-17: Findings panel, revision bar, and compliance/edit mutations removed (routes not in contract).
 * Findings are read only; accept/reject and element-move mutations awaiting implementation.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ElementFilter } from "../../../api";
import {
  RequestRefused,
  elementMatchesFilter,
  filterIsActive,
  filterToSearch,
} from "../../../api";
import {
  AbsenceBadge,
  ActionButton,
  CategorySwatch,
  ComplianceTag,
  HueSwatch,
  Label,
  Mono,
  Notice,
  NumberInput,
  Overlay,
  OverlayBlock,
  OverlayColumn,
  Ramp,
  Rule,
  Segment,
  Stack,
  Stage,
  Text,
  usePaints,
} from "../../../strata";
import { LUX_SCALE_MAX, useIlluminance } from "../lighting";
import { useFindings } from "../redline";
import {
  useCircuits,
  useCallouts,
  useFlows,
  useOutlets,
  useScope,
  useScopeView,
  type Drawable,
  type FindingView,
  textAttribute,
  isWhole,
} from "../queries";
import { circuitColor, circuitLabel } from "../circuits";
import { catalogOf, classCatalogOf, roleText, planeCountsOf } from "../catalog";
import { CanvasVisualizer, type Pin } from "./CanvasVisualizer";
import { SceneView } from "./SceneView";

/** Type guard to identify a whole ViewerElement (has raw_attributes and object_type). */
/** Extract the subject key (element id) from a finding's dimensions, or null if absent. */
function subjectOf(f: FindingView): string | null {
  return (f.dimensions as Record<string, unknown>)?.element as string | null;
}

/** A finding is open when it's a breach and hasn't been rejected. */
function isOpen(f: FindingView): boolean {
  return f.breach && f.ruling !== "rejected";
}

export function useElementsStatus(projectId: string) {
  // Read from the scope view at the project rung: the subject.members count.
  const filter = useSearch({ strict: false }) as ElementFilter;
  const scope = filter.scope ?? projectId;
  const {
    data: scopeView,
    error,
    isError,
    isSuccess,
  } = useScopeView(projectId, scope);
  // isSuccess, never "not loading": a query can sit pending-but-PAUSED
  // (TanStack parks retries while the tab is unfocused or the browser
  // thinks it's offline), and treating that limbo as success painted a
  // lying green "0 elements loaded" while the server was asleep.
  // WHICH KIND of failure (2026-09-11): a refusal of the CALLER is not an unreachable server, and
  // the shell reported both as "cannot reach the server" -- blaming the network for an expired
  // token or an account with no membership, which is a thing the reader can actually act on.
  const refusedIdentity = error instanceof RequestRefused && error.isIdentity;
  return {
    count: scopeView?.subject.members ?? 0,
    isError,
    isSettled: isSuccess,
    refusedIdentity,
  };
}

/** A LineString's plan run, vertical drop, and grade -- the live slope
 * chip's numbers, computed from the element's own geometry. */
function slopeOf(
  el: Drawable,
): { runM: number; dropM: number; pct: number } | null {
  const geom = el.geometry;
  if (!geom || geom.type !== "LineString") return null;
  const coords = geom.coordinates as number[][];
  if (!coords || coords.length < 2) return null;
  let runM = 0;
  let [previousX, previousY] = coords[0];
  for (const [x, y] of coords.slice(1)) {
    runM += Math.hypot(x - previousX, y - previousY);
    previousX = x;
    previousY = y;
  }
  const dropM = Math.abs(
    (coords[coords.length - 1][2] ?? 0) - (coords[0][2] ?? 0),
  );
  if (runM === 0) return null;
  return { runM, dropM, pct: (dropM / runM) * 100 };
}

export function ViewerPanel({ projectId }: { projectId: string }) {
  // The scope ladder (DESIGN_PRINCIPLES 33): the URL holds the current rung; useScope
  // manages both reading and writing it.
  const { scope, setScope } = useScope(projectId);
  const {
    data: scopeView,
    error: scopeError,
    isError: isScopeError,
  } = useScopeView(projectId, scope);

  // Extract elements from the scope view based on its depth. At depth "whole", use the elements
  // array; at depth "rows", map thin rows to a drawable shape; at depth "counts", no per-element
  // draw (the canvas instead draws the children's footprints).
  const elements = useMemo(() => {
    if (!scopeView) return [];
    if (scopeView.depth === "whole") {
      return scopeView.elements.map((el) => ({
        ...el,
        // Ensure the shape has all the fields expected by elementMatchesFilter and other consumers
        category: el.category ?? null,
      }));
    }
    if (scopeView.depth === "rows") {
      // Map ThinRow to a Drawable: origin_id, category, geometry. The canvas uses these
      // to test filter matching and for rendering, neither of which need the full element details.
      // At the card, we use isWhole() to check if it's a full ViewerElement; if not, we show
      // just the ifc_class from the thin row.
      return scopeView.rows.map(
        (row) =>
          ({
            origin_id: row.origin_id,
            category: row.category ?? null,
            geometry: row.geometry,
          }) as Drawable,
      );
    }
    // depth === 'counts': no elements to draw per se; the canvas draws children footprints instead.
    return [];
  }, [scopeView]);

  // The children of the current rung: offer descent into each.
  const children = scopeView?.children ?? [];

  const { data: callouts = [] } = useCallouts(projectId, scope);
  const { data: outlets = [] } = useOutlets(projectId, scope);
  // THE FLOW LANE (2026-09-13): the system as stored, drawn when the toggle is on; with the
  // category filter on ELECTRICAL it is the electrical system alone, its flow and its items.
  const { data: flow = null } = useFlows(projectId, scope);
  const [flowOn, setFlowOn] = useState(false);
  const { data: circuits = null } = useCircuits(projectId, scope);
  const [circuitsOn, setCircuitsOn] = useState(false);
  // THE CATALOG (2026-09-13, owner: "there is just so much to take in at once"): the visible items
  // as a list by kind, each kind's count, and with the flow on its roles and potential; a row
  // isolates its kind by peeling the rest (the legend's restore undoes it), so the picture can be
  // taken in one kind at a time.
  const [catalogOpen, setCatalogOpen] = useState(true);
  const [isolatedKind, setIsolatedKind] = useState<string | null>(null);
  // The drawing's notes and the engine's proposed outlets share one pin lane: each says what it is.
  const pins = useMemo<Pin[]>(
    () => [
      ...callouts.map((c) => ({
        x: c.x,
        y: c.y,
        z: c.z,
        kind: "note" as const,
        code: false,
        gfci: false,
        text: c.text,
      })),
      ...outlets.map((o) => ({
        x: o.x,
        y: o.y,
        z: o.z,
        kind: o.kind,
        code: o.standing === "code",
        gfci: o.gfci,
        text: o.text,
      })),
    ],
    [callouts, outlets],
  );
  const { data: findings = [] } = useFindings(projectId);
  const [selected, setSelected] = useState<Drawable | null>(null);
  // The filter IS the URL (APP_SPEC "Filtering is first-class"): the
  // RuleSelector search params the shell's route types; the legend
  // below is the category chip's visual handle. Read via useSearch --
  // no module ever imports the shell.
  const filter = useSearch({ strict: false }) as ElementFilter;
  const navigate = useNavigate();
  const filtered = filterIsActive(filter);
  // The illuminance heatmap layer: part of the DEFAULT VIEW when the
  // project carries real rooms (user ruling 2026-08-04) -- luxPref
  // null means AUTO (on iff authored IfcSpace rooms exist); the
  // toggle records an explicit choice. MEP-only projects stay off by
  // default (one building-scope wash is heavy as an opener).
  const [luxPref, setLuxPref] = useState<boolean | null>(null);
  const [sceneViewOn, setSceneViewOn] = useState(false);
  const hasRooms = useMemo(
    () =>
      elements.some(
        (el) =>
          isWhole(el) &&
          (el.raw_attributes as { ifc_class?: string } | undefined)
            ?.ifc_class === "IfcSpace",
      ),
    [elements],
  );
  const luxOn = luxPref ?? hasRooms;
  const { data: luxRooms = [] } = useIlluminance(projectId, luxOn);
  // Drilling (APP_SPEC): peels are session gestures (module-private
  // client state by design), and excluded elements GHOST by default
  // -- the toggle flips ghost -> hidden for a clean view.
  const [peeled, setPeeled] = useState<Set<string>>(new Set());
  const [ghostOn, setGhostOn] = useState(true);
  // Snap-to-interest: picking from a LIST jumps the camera to the
  // element (canvas clicks never move the camera -- you're already
  // looking there). OWED: implement focus target on scope descent.
  const [focusTarget] = useState<{
    originId: string;
    nonce: number;
  } | null>(null);

  // A new project (or a re-ingest or a scope change) invalidates any prior selection.
  useEffect(() => {
    setSelected(null);
    setLuxPref(null); // back to AUTO: the next project decides itself
    setPeeled(new Set());
    setIsolatedKind(null);
  }, [projectId, scope]);
  // A selection the filter no longer shows (or a peel just hid) would
  // be an invisible drag target -- drop it.
  useEffect(() => {
    setSelected((prev) =>
      prev &&
      (!elementMatchesFilter(prev, filter) || peeled.has(prev.origin_id))
        ? null
        : prev,
    );
  }, [filter, peeled]);
  // Refetched elements carry moved geometry -- keep the selection
  // pointed at the fresh row, not a stale snapshot.
  useEffect(() => {
    setSelected((prev) =>
      prev
        ? (elements.find((el) => el.origin_id === prev.origin_id) ?? null)
        : null,
    );
  }, [elements]);

  const presentSystemTypes = useMemo(() => {
    const counts = new Map<string, number>();
    for (const el of elements) {
      const key = el.category ?? "OTHER";
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [elements]);

  const visibleElements = useMemo(
    () =>
      elements.filter(
        (el) => elementMatchesFilter(el, filter) && !peeled.has(el.origin_id),
      ),
    [elements, filter, peeled],
  );
  const visibleIds = useMemo(
    () => new Set(visibleElements.map((el) => el.origin_id)),
    [visibleElements],
  );
  // The plane counts at depth "counts": the breakdown by category from the server.
  const planeCounts = useMemo(
    () => (scopeView?.depth === "counts" ? planeCountsOf(scopeView) : null),
    [scopeView],
  );

  // The catalog depends on depth. At depth "counts", no catalog (the plane counts replace it).
  // At depth "rows" or "whole", show the standard catalog of kinds.
  const catalog = useMemo(() => {
    if (!scopeView || scopeView.depth === "counts") return [];
    if (scopeView.depth === "rows") {
      // A thin row has no authored name: the kind is its IFC class on its plane, as the server sent it.
      const wanted = filter.category;
      return classCatalogOf(
        scopeView.rows.filter(
          (row) => !wanted?.length || wanted.includes(row.category ?? "OTHER"),
        ),
      );
    }
    return catalogOf(
      elements.filter((el) => elementMatchesFilter(el, filter)),
      flowOn ? flow : null,
    );
  }, [elements, filter, flowOn, flow, scopeView]);

  const isolateKind = (key: string) => {
    if (isolatedKind === key) {
      setIsolatedKind(null);
      setPeeled(new Set());
      return;
    }
    const keep = new Set(
      catalog.find((row) => row.key === key)?.originIds ?? [],
    );
    setIsolatedKind(key);
    setPeeled(
      new Set(
        elements
          .filter((el) => !keep.has(el.origin_id))
          .map((el) => el.origin_id),
      ),
    );
  };
  // Ghost mode: the canvas draws EVERYTHING, with the excluded set at
  // low opacity; hidden mode passes only the visible subset.
  const ghostIds = useMemo(() => {
    if (!ghostOn) return undefined;
    const ids = new Set<string>();
    for (const el of elements)
      if (!visibleIds.has(el.origin_id)) ids.add(el.origin_id);
    return ids.size > 0 ? ids : undefined;
  }, [ghostOn, elements, visibleIds]);
  // Every screen honors the same filter: findings narrow with the
  // canvas, and the bulk verbs below act on exactly this set.
  const visibleFindings = useMemo(
    () =>
      filtered
        ? findings.filter((f) => {
            const subject = subjectOf(f);
            return subject && visibleIds.has(subject);
          })
        : findings,
    [filtered, findings, visibleIds],
  );
  const violatingIds = useMemo(
    () =>
      new Set(
        visibleFindings
          .filter(isOpen)
          .map(subjectOf)
          .filter((s): s is string => s !== null),
      ),
    [visibleFindings],
  );
  // The lighting redline as GEOGRAPHY: the open min-illuminance
  // findings supply the failing floor (their own required value --
  // never a frontend constant) and each room's worst point, so the
  // canvas can paint deficit cells red and pin the exact spots.
  const luxRedline = useMemo(() => {
    const lighting = visibleFindings.filter(
      (f) => isOpen(f) && f.rule_name === "ies-rp-1-min-illuminance",
    );
    if (lighting.length === 0) return null;
    const floors = lighting
      .map((f) => (f.bounds as { min_lux?: number }).min_lux)
      .filter((v): v is number => typeof v === "number");
    const worstPoints = lighting
      .map(
        (f) => (f.dimensions as { worst_point?: [number, number] }).worst_point,
      )
      .filter((p): p is [number, number] => Array.isArray(p) && p.length === 2);
    if (floors.length === 0) return null;
    return { floorLux: Math.max(...floors), worstPoints };
  }, [visibleFindings]);

  const typeHidden = (systemType: string) =>
    filter.category ? !filter.category.includes(systemType) : false;

  // The legend writes the URL: toggling recomputes the include list;
  // a list covering every present type drops the param (clean URL).
  const toggleType = (systemType: string) => {
    const present = presentSystemTypes.map(([t]) => t);
    const included = filter.category ?? present;
    const next = included.includes(systemType)
      ? included.filter((t) => t !== systemType)
      : [...included, systemType];
    const complete = present.every((t) => next.includes(t));
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        ...filterToSearch({ ...filter, category: complete ? undefined : next }),
      }),
      replace: true,
    });
  };

  // The elevation band writes the URL like every other chip: the
  // height_band vocabulary's own params, empty input clears a bound.
  const setBand = (key: "min_z_meters" | "max_z_meters", value: string) => {
    const parsed = value === "" ? NaN : Number(value);
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        ...filterToSearch({
          ...filter,
          [key]: Number.isFinite(parsed) ? parsed : undefined,
        }),
      }),
      replace: true,
    });
  };

  const slope = selected ? slopeOf(selected) : null;

  // The popup tells the element's WHOLE story (user-ruled):
  // its own findings ride the already-fetched findings query, and
  // when the illuminance layer is on, the nearest work-plane sample
  // says how much light lands where this element stands.
  const selectedFindings = useMemo(
    () =>
      selected
        ? findings.filter(
            (f) => subjectOf(f) === selected.origin_id && isOpen(f),
          )
        : [],
    [selected, findings],
  );
  const luxHere = useMemo(() => {
    if (!selected || !luxOn || luxRooms.length === 0) return null;
    const geom = selected.geometry;
    const first = (c: unknown): number[] | null =>
      Array.isArray(c)
        ? typeof c[0] === "number"
          ? (c as number[])
          : first(c[0])
        : null;
    const at = first(geom?.coordinates);
    if (!at) return null;
    let best: number | null = null;
    let bestDist = 0.75; // within 1.5 grid cells of the sample lattice
    for (const room of luxRooms) {
      for (const [px, py, lux] of room.points) {
        const dist = Math.hypot(px - at[0], py - at[1]);
        if (dist < bestDist) {
          bestDist = dist;
          best = lux;
        }
      }
    }
    return best;
  }, [selected, luxOn, luxRooms]);

  const luxPoints = useMemo(
    () => (luxOn ? luxRooms.flatMap((room) => room.points) : null),
    [luxOn, luxRooms],
  );
  const luxSummary = useMemo(() => {
    if (!luxOn || luxRooms.length === 0) return null;
    return {
      rooms: luxRooms.length,
      min: Math.min(...luxRooms.map((r) => r.min_lux)),
      avg: luxRooms.reduce((s, r) => s + r.avg_lux, 0) / luxRooms.length,
    };
  }, [luxOn, luxRooms]);

  const paints = usePaints();
  // The three lanes the legend's strip toggles, as one multi-select
  // segment: the URL carries the category filter, these are session
  // gestures (module-private client state by design).
  const laneKeys = [
    ...(ghostOn ? [] : ["hidden"]),
    ...(flowOn ? ["flow"] : []),
    ...(circuitsOn ? ["circuits"] : []),
  ];
  const setLanes = (keys: Set<string>) => {
    setGhostOn(!keys.has("hidden"));
    setFlowOn(keys.has("flow"));
    setCircuitsOn(keys.has("circuits"));
  };

  return (
    <>
      {isScopeError && scopeError && (
        <Overlay anchor="top-center" dense>
          <Notice kind="error">
            {scopeError instanceof RequestRefused && scopeError.isIdentity
              ? "Access denied"
              : "Unable to load this rung"}
          </Notice>
        </Overlay>
      )}

      {/* Scope navigation: show current rung and offer descent to children */}
      {scopeView && (
        <Overlay anchor="top-left" width={300} maxHeight="40vh" ariaLabel="Scope">
          <Label>{scopeView.subject.rung}</Label>
          <Text size="control" weight={500}>
            {scopeView.subject.name || scopeView.subject.origin_id}
          </Text>
          {scope !== projectId && (
            <div>
              <ActionButton size="sm" onPress={() => setScope(projectId)}>
                ← Project
              </ActionButton>
            </div>
          )}
          {children.length > 0 && (
            <Stack direction="column" gap="xs">
              <Text size="caption" tone="muted">
                Descend into:
              </Text>
              {children.map((child) => (
                <ActionButton
                  key={child.origin_id}
                  size="sm"
                  variant="bare"
                  onPress={() => setScope(child.origin_id)}
                  aria-label={child.name || `${child.members} elements`}
                >
                  {child.name || "(Unnamed)"}
                  <Text size="caption" tone="muted">
                    ({child.members})
                  </Text>
                </ActionButton>
              ))}
            </Stack>
          )}
        </Overlay>
      )}

      {/* View mode toggle */}
      <Overlay anchor="top-center" dense>
        <Segment
          size="sm"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={[sceneViewOn ? "scene" : "plan"]}
          onSelectionChange={(keys) => setSceneViewOn(Array.from(keys)[0] === "scene")}
          options={[
            { key: "plan", label: "Plan" },
            { key: "scene", label: "Scene" },
          ]}
        />
      </Overlay>

      {/* Illuminance, bottom right -- or the key, when a lane is on */}
      {flowOn || circuitsOn ? (
        <Overlay anchor="bottom-right" width={320} maxHeight="60vh" ariaLabel="Key">
          <Label>Key</Label>
          {flowOn && (
            <Stack direction="column" gap="xs">
              <Stack direction="row" gap="sm" align="center">
                <svg width="34" height="12" aria-hidden="true">
                  <line x1="1" y1="6" x2="33" y2="6" stroke={paints.action} strokeWidth="1.5" />
                  <polyline points="14,2 18,6 14,10" fill="none" stroke={paints.action} strokeWidth="1.5" />
                  <polygon points="33,6 27,3 27,9" fill={paints.action} />
                </svg>
                <Text size="caption">flow direction: hookup → loads (chevron and arrowhead point the way)</Text>
              </Stack>
              <Stack direction="row" gap="sm" align="center">
                <svg width="34" height="12" aria-hidden="true">
                  <line x1="1" y1="6" x2="33" y2="6" stroke={paints.categoryDefault} strokeWidth="1" strokeDasharray="4 3" />
                </svg>
                <Text size="caption" tone="secondary">
                  no direction reached (unoriented residue)
                </Text>
              </Stack>
              <Text size="caption" tone="secondary">
                bright = near the hookup, dim = at the loads; S source · P process · K sink
              </Text>
            </Stack>
          )}
          {circuitsOn && (circuits?.circuits.length ?? 0) > 0 && (
            <Stack direction="column" gap="xs">
              {circuits?.circuits.map((circuit, index) => (
                <Stack key={circuit.origin_id} direction="row" gap="sm" align="center">
                  <HueSwatch color={circuitColor(index)} />
                  <Text size="caption">{circuitLabel(circuit, index)}</Text>
                  <Text size="caption" tone="muted">
                    ({circuit.members.length})
                  </Text>
                </Stack>
              ))}
              <Text size="caption" tone="secondary">
                faded = claimed by no circuit
              </Text>
            </Stack>
          )}
          {circuitsOn && (circuits?.circuits.length ?? 0) === 0 && (
            <Text size="caption" tone="secondary">
              this file draws no circuits
            </Text>
          )}
        </Overlay>
      ) : (
        <Overlay anchor="bottom-right" dense>
          <div>
            <ActionButton
              size="sm"
              variant={luxOn ? "filled" : "quiet"}
              paint={luxOn ? paints.absence : undefined}
              onPress={() => setLuxPref(!luxOn)}
              aria-label="Illuminance"
            >
              {luxOn ? "Illuminance on" : "Illuminance"}
            </ActionButton>
          </div>
          {luxSummary && (
            <Stack direction="column" gap="xs">
              <LuxRamp />
              <Stack direction="row" gap="sm" justify="space-between">
                <Text size="caption" tone="secondary">
                  0 lx
                </Text>
                <Text size="caption" tone="secondary">
                  {LUX_SCALE_MAX}+ lx
                </Text>
              </Stack>
              <Text size="caption">
                {luxSummary.rooms} {luxSummary.rooms === 1 ? "zone" : "rooms"} · min {luxSummary.min.toFixed(0)} lx · avg{" "}
                {luxSummary.avg.toFixed(0)} lx
              </Text>
            </Stack>
          )}
        </Overlay>
      )}

      {presentSystemTypes.length > 0 && (
        <Overlay anchor="bottom-left" width={300} maxHeight="60vh" ariaLabel="System types">
          <Label>System types</Label>
          <Stack direction="column" gap="xs">
            {presentSystemTypes.map(([systemType, count]) => (
              <ActionButton
                key={systemType}
                size="sm"
                variant="bare"
                onPress={() => toggleType(systemType)}
                aria-label={typeHidden(systemType) ? `Show ${systemType}` : `Hide ${systemType}`}
              >
                <CategorySwatch category={systemType} />
                <Text size="control" tone={typeHidden(systemType) ? "muted" : "ink"}>
                  {typeHidden(systemType) ? <s>{systemType}</s> : systemType}
                </Text>
                <Text size="caption" tone="muted">
                  ({count})
                </Text>
              </ActionButton>
            ))}
          </Stack>
          <Rule />
          <Label>Elevation band (m)</Label>
          <Stack direction="row" gap="xs" align="center">
            <NumberInput
              label="Elevation band minimum, meters"
              labelHidden
              size="sm"
              step={0.1}
              placeholder="min"
              value={filter.min_z_meters ?? NaN}
              onChange={(v) => setBand("min_z_meters", Number.isFinite(v) ? String(v) : "")}
            />
            <Text tone="muted">–</Text>
            <NumberInput
              label="Elevation band maximum, meters"
              labelHidden
              size="sm"
              step={0.1}
              placeholder="max"
              value={filter.max_z_meters ?? NaN}
              onChange={(v) => setBand("max_z_meters", Number.isFinite(v) ? String(v) : "")}
            />
          </Stack>
          <Segment
            size="sm"
            selectionMode="multiple"
            selectedKeys={laneKeys}
            onSelectionChange={(keys) => setLanes(new Set(Array.from(keys, String)))}
            options={[
              { key: "hidden", label: `excluded: ${ghostOn ? "ghost" : "hidden"}`, ariaLabel: "excluded" },
              { key: "flow", label: `flow: ${flowOn ? `on · ${flow?.edges.length ?? 0} couplings` : "off"}`, ariaLabel: "flow" },
              { key: "circuits", label: `circuits: ${circuitsOn ? `on · ${circuits?.circuits.length ?? 0}` : "off"}`, ariaLabel: "circuits" },
            ]}
          />
          {peeled.size > 0 && (
            <div>
              <ActionButton
                size="sm"
                paint={paints.absence}
                onPress={() => {
                  setPeeled(new Set());
                  setIsolatedKind(null);
                }}
              >
                {peeled.size} peeled -- restore
              </ActionButton>
            </div>
          )}
        </Overlay>
      )}

      <OverlayColumn anchor="top-right" width={300}>
        {/* Plane counts at depth "counts" */}
        {planeCounts && (
          <OverlayBlock ariaLabel="Counts">
            <Label>Counts · {planeCounts.planes.length} planes</Label>
            <Stack direction="column" gap="xs">
              {planeCounts.planes.map((plane) => (
                <Stack key={plane.category} direction="row" gap="sm" align="center" justify="space-between">
                  <Stack direction="row" gap="sm" align="center">
                    <CategorySwatch category={plane.category} />
                    <Text size="caption">{plane.category}</Text>
                  </Stack>
                  <Text size="caption" tone="secondary">
                    ×{plane.count}
                  </Text>
                </Stack>
              ))}
              {planeCounts.unclassified > 0 && (
                <>
                  <Rule />
                  <Stack direction="row" gap="sm" align="center" justify="space-between">
                    <Text size="caption" tone="secondary">
                      Unclassified
                    </Text>
                    <Text size="caption" tone="secondary">
                      ×{planeCounts.unclassified}
                    </Text>
                  </Stack>
                </>
              )}
            </Stack>
          </OverlayBlock>
        )}

        {/* Catalog at depth "rows" or "whole" */}
        {catalog.length > 0 && (
          <OverlayBlock ariaLabel="Catalog">
            <ActionButton size="sm" variant="bare" onPress={() => setCatalogOpen((v) => !v)} aria-expanded={catalogOpen}>
              <Label>
                Catalog · {catalog.length} kinds · {catalog.reduce((n, row) => n + row.originIds.length, 0)} items
              </Label>
              <Text size="caption" tone="secondary">
                {catalogOpen ? "−" : "+"}
              </Text>
            </ActionButton>
            {catalogOpen && (
              <Stack direction="column" gap="xs">
                {catalog.map((row) => {
                  const isolated = isolatedKind === row.key;
                  const roles = roleText(row);
                  return (
                    <ActionButton
                      key={row.key}
                      size="sm"
                      variant={isolated ? "filled" : "bare"}
                      paint={isolated ? paints.absence : undefined}
                      onPress={() => isolateKind(row.key)}
                      aria-label={isolated ? "Show everything again" : "Show this kind alone"}
                      isDisabled={false}
                    >
                      <CategorySwatch category={row.category} />
                      <Text size="caption" tone={isolatedKind && !isolated ? "muted" : "ink"} ellipsis>
                        {row.name}{" "}
                        <Text size="micro" tone="muted">
                          {row.typeClass.replace(/^Ifc|Type$/g, "")}
                        </Text>
                      </Text>
                      <Text size="micro" tone="secondary">
                        {roles && (
                          <Text size="micro" tone="ink">
                            {roles}{" "}
                          </Text>
                        )}
                        {row.phi != null && <>φ {row.phi.toFixed(2)} </>}×{row.originIds.length}
                      </Text>
                    </ActionButton>
                  );
                })}
              </Stack>
            )}
          </OverlayBlock>
        )}

        {selected && (
          <OverlayBlock ariaLabel="Selected element">
            <Stack direction="row" gap="sm" align="flex-start" justify="space-between">
              <Label>Selected element</Label>
              <ActionButton size="sm" onPress={() => setSelected(null)} aria-label="Close">
                ×
              </ActionButton>
            </Stack>
            <Stack direction="column" gap="xs">
              <Detail term="IFC class">
                {isWhole(selected)
                  ? ((selected.raw_attributes as { ifc_class?: string } | undefined)?.ifc_class ?? "--")
                  : selected.ifc_class}
              </Detail>
              {isWhole(selected) && (
                <>
                  <Detail term="Name">{textAttribute(selected, "element_name") ?? "--"}</Detail>
                  <Detail term="System type">{selected.category_display ?? "--"}</Detail>
                </>
              )}
              {slope && (
                <Detail term="Slope">
                  <ComplianceTag state={violatingIds.has(selected.origin_id) ? "violating" : "complying"}>
                    {slope.pct.toFixed(2)}% · {slope.dropM.toFixed(3)} m over {slope.runM.toFixed(2)} m
                  </ComplianceTag>
                </Detail>
              )}
              {isWhole(selected) && (
                <>
                  {selected.object_type && (
                    <Detail term="Object type">
                      <Text size="control" breakAll>
                        {selected.object_type}
                      </Text>
                    </Detail>
                  )}
                  {selected.tag && <Detail term="Tag">{selected.tag}</Detail>}
                  {selected.authoring_tool && selected.authoring_tool !== "UNKNOWN" && (
                    <Detail term="Authored in">{selected.authoring_tool}</Detail>
                  )}
                </>
              )}
              {luxHere !== null && (
                <Detail term="Illuminance here">
                  <AbsenceBadge label={`≈ ${Math.round(luxHere)} lx on the work plane`} reason="nearest work-plane sample" />
                </Detail>
              )}
              {selectedFindings.length > 0 && (
                <Detail term={`Open findings (${selectedFindings.length})`}>
                  <Stack direction="column" gap="xs">
                    {selectedFindings.map((f, i) => (
                      <ComplianceTag key={`${f.rule_name}-${f.measurement}-${i}`} state="violating">
                        {f.rule_name} -- {f.value} {f.unit} vs bounds
                      </ComplianceTag>
                    ))}
                  </Stack>
                </Detail>
              )}
              <Detail term="Origin ID">
                <Mono tone="ink" size="caption">
                  {selected.origin_id}
                </Mono>
              </Detail>
              {isWhole(selected) && selected.is_proxy && (
                <AbsenceBadge label="proxy" reason="Tier 3 vector-fallback classification -- no confident ruling yet" />
              )}
              <div>
                <ActionButton
                  size="sm"
                  paint={paints.absence}
                  onPress={() => setPeeled((prev) => new Set(prev).add(selected.origin_id))}
                  aria-label="Peel away: hide this element to reveal what it covers (session-only; restore from the legend)"
                >
                  Peel away
                </ActionButton>
              </div>
              <Text size="caption" tone="secondary">
                Drag the highlighted element to move it; the redline re-checks on drop.
              </Text>
            </Stack>
          </OverlayBlock>
        )}
      </OverlayColumn>

      <Stage>
        {sceneViewOn ? (
          <SceneView projectId={projectId} scopeView={scopeView} />
        ) : (
          <CanvasVisualizer
            elements={elements}
            scopeView={scopeView}
            visibleIds={visibleIds}
            ghostIds={ghostIds}
            focusTarget={focusTarget}
            illuminancePoints={luxPoints}
            luxRedline={luxRedline}
            callouts={pins}
            flow={flowOn ? flow : null}
            circuits={circuitsOn ? circuits : null}
            onElementClick={setSelected}
            onScopeChange={setScope}
            violatingIds={violatingIds}
            selectedOriginId={selected?.origin_id ?? null}
            onElementMoved={(_originId, _dx, _dy) => {
              // Element movement mutations not in contract (2026-09-17)
            }}
          />
        )}
      </Stage>
    </>
  );
}

/** One term/definition pair of the inspector. */
function Detail({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <Stack direction="column" gap="xs">
      <Text size="micro" tone="muted">
        {term}
      </Text>
      <Text size="control">{children}</Text>
    </Stack>
  );
}

/** The illuminance ramp: the absence paint from dim to bright, the
 * canvas's heatmap scale in miniature. */
function LuxRamp() {
  const paints = usePaints();
  return <Ramp paint={paints.absence} />;
}
